import { useState, useCallback } from 'react';
import { userGroupsService } from '@modules/userGroups/services/userGroupsService';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import { useDeleteHandler } from '@shared/hooks/useDeleteHandler';
import { useEditHandler } from '@shared/hooks/useEditHandler';

export const useGroupTable = ({
    fetchData,
    selectedData,
    data,
    handleOpenModal,
    handleCloseModal,
    handleError,
    setSelectedData,
}) => {
    const [groupData, setGroupData] = useState(null);
    const defaultSortBy = 'name';

    const getData = userGroupsService.getGroups;
    const searchOptions = [
        { key: 'name', label: 'groupName' },
        { key: 'ldapGroup', label: 'LDAP' }
    ];

    const selectedGroupName = useCallback(() =>
        selectedData !== null ? data.find(group => group.id === selectedData)?.name : '',
        [selectedData, data]);

    const handleEditGroup = useEditHandler({
        data,
        selectedData,
        setSelectedItem: setGroupData,
        handleOpenModal: () => handleOpenModal('add'),
        modalType: 'edit',
        errorMessage: 'No group selected for editing'
    }); 

    const handleAddCloseModal = useCallback(() => {
        handleCloseModal();
        setGroupData(null);
        fetchData(userGroupsService.getGroups, {
            sortBy: defaultSortBy,
            sortDir: 'asc'
        });
    }, [handleCloseModal, fetchData]);

    const handleCloseUsersModal = useCallback(() => {
        handleCloseModal();
        fetchData(userGroupsService.getGroups, {
            sortBy: defaultSortBy,
            sortDir: 'asc'
        });
    }, [handleCloseModal, fetchData]);

    const handleConfirmDelete = useDeleteHandler({
        service: userGroupsService,
        entityName: 'Group',
        selectedData,
        fetchData,
        setSelectedData,
        handleCloseModal,
        handleError
    });

    const handleSaveMenus = useCallback(async (menuData) => {
        if (!selectedData) {
            notifyError('No group selected');
            return;
        }

        try {
            await userGroupsService.updateGroup(selectedData, {
                ...data.find(group => group.id === selectedData),
                specRights: menuData.specRights.map(item => item.id)
                
            });
            notifySuccess('Menus updated successfully');
            handleCloseModal();
            fetchData(userGroupsService.getGroups, { 
                sortBy: defaultSortBy, 
                sortDir: 'asc' 
            });
        } catch (error) {
            console.log(menuData, "menu data");

            handleError('Failed to update menus', error);
        }
    }, [selectedData, data, handleCloseModal, fetchData, handleError]);

    const handleOpenMenu = useCallback(async () => {
        if (!selectedData) {
            notifyError('No group selected');
            return;
        }
        try {
            const fetchedGroupData = await userGroupsService.getGroupById(selectedData);
            setGroupData(fetchedGroupData);
            handleOpenModal('menu');
        } catch (error) {
            handleError('Failed to fetch group data', error);
        }
    }, [selectedData, handleOpenModal, handleError]);

    return {
        groupData,
        setGroupData,
        getData,
        searchOptions,
        selectedGroupName: selectedGroupName(),
        handleEditGroup,
        handleAddCloseModal,
        handleConfirmDelete,
        handleSaveMenus,
        handleOpenMenu,
        handleCloseUsersModal
    };
}; 